// R58-L-14 — Tiles widget scope + kind multi-select store.
//
// Two pieces of UI state persisted to URL:
//   * `tilesScope` — `current` | `all`. Per OQ-10, default = current
//     image. Independent of the List widget's same toggle.
//   * `tilesKinds` — comma-separated subset of `ai,defect`. Default
//     = both checked.
//
// The store + URL-sync are split to keep the bare store easily
// testable from a non-router context.
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { create } from "zustand";

export type TilesScope = "current" | "all";
export type TilesKind = "ai" | "defect";

export interface TilesScopeState {
  scope: TilesScope;
  kinds: ReadonlySet<TilesKind>;
  setScope: (next: TilesScope) => void;
  toggleKind: (kind: TilesKind) => void;
  hydrate: (state: {
    scope: TilesScope;
    kinds: ReadonlySet<TilesKind>;
  }) => void;
}

const DEFAULT_KINDS: ReadonlySet<TilesKind> = new Set<TilesKind>([
  "ai",
  "defect",
]);

export const useTilesScope = create<TilesScopeState>((set) => ({
  scope: "current",
  kinds: DEFAULT_KINDS,
  setScope: (next) => {
    set({ scope: next });
  },
  toggleKind: (kind) => {
    set((s) => {
      const next = new Set(s.kinds);
      if (next.has(kind)) {
        // Refuse to empty the set — pressing the only-checked chip is a
        // no-op so the widget never enters an unreadable empty state.
        if (next.size === 1) return s;
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return { kinds: next };
    });
  },
  hydrate: ({ scope, kinds }) => {
    set({ scope, kinds });
  },
}));

function parseScope(raw: string | null): TilesScope {
  return raw === "all" ? "all" : "current";
}

function parseKinds(raw: string | null): ReadonlySet<TilesKind> {
  if (raw === null) return DEFAULT_KINDS;
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is TilesKind => s === "ai" || s === "defect");
  if (parts.length === 0) return DEFAULT_KINDS;
  return new Set(parts);
}

function serialiseKinds(kinds: ReadonlySet<TilesKind>): string {
  const out: TilesKind[] = [];
  if (kinds.has("ai")) out.push("ai");
  if (kinds.has("defect")) out.push("defect");
  return out.join(",");
}

export function useTilesScopeUrlSync(): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = useTilesScope((s) => s.scope);
  const kinds = useTilesScope((s) => s.kinds);
  const hydrate = useTilesScope((s) => s.hydrate);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    hydrate({
      scope: parseScope(searchParams.get("tilesScope")),
      kinds: parseKinds(searchParams.get("tilesKinds")),
    });
  }, [searchParams, hydrate]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("tilesScope", scope);
    next.set("tilesKinds", serialiseKinds(kinds));
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [scope, kinds, searchParams, setSearchParams]);
}
